/**
 * Menu — the one panel a player is meant to open. M toggles it.
 *
 * It replaces a permanently-lit corner legend that told you the controls whether you wanted
 * them or not. A legend you cannot dismiss is a legend you stop reading after the first
 * minute, and it is on screen for every screenshot ever taken of the game.
 *
 * Audio is the reason this exists. The mix has two buses that were already separate in the
 * engine — music and ambience — so a slider and a switch on each is the whole feature; the
 * engine's own tuned levels stay in the engine and these scale them rather than replacing
 * them, so turning music to 100% gives the mix its author intended and not a brick wall.
 *
 * Settings persist. A player who turns the music off means it, and meaning it once should be
 * enough. localStorage is wrapped because it throws outright in a partitioned iframe or with
 * site data blocked, and a settings panel is not worth taking the game down over.
 */
import type { AudioEngine } from '../audio/AudioEngine.ts';
import { formatBay } from '../../shared/protocol.js';
import { inviteUrl } from './Landing.ts';

const STORE_KEY = 'shoreplay.settings.v1';

/** what the panel lists as the controls. Kept next to the bindings it describes. */
const CONTROLS: Array<[string, string]> = [
  ['W / S', 'throttle'],
  ['A / D', 'turn'],
  ['SHIFT or E', 'dive'],
  ['Q or R', 'surface'],
  ['SPACE', 'lunge'],
  /* [ and ] still cycle species — see main.ts — but they are not listed. Swapping which animal
     you are is a thing the landing flow will own, not a key you find by reading a legend. */
  ['MOUSE', 'aim'],
  ['M', 'this menu'],
  ['~ or F3', 'diagnostics'],
];

interface Saved {
  musicVol: number;
  sfxVol: number;
  musicOn: boolean;
  sfxOn: boolean;
  /** set the first time the nudge has been shown, so it is only ever shown once */
  seenNudge: boolean;
}

/* The first-run nudge. Late enough that the world has drawn and the eye has somewhere to come
   back FROM — a hint already on screen at frame one is part of the loading screen and gets read
   as furniture.

   It then STAYS until it is dismissed. It used to time out after seven seconds, which is long
   enough to notice something appeared and not long enough to read it, decide it matters and
   act on it — and once it had gone there was no way to get it back. A close button is the
   honest version: it says the thing will wait, and it makes going away the player's decision
   rather than a race against a timer. This is not the always-on legend coming back, because
   that legend could not be dismissed and this shows once ever. */
const NUDGE_IN_MS = 1600;

function load(): Partial<Saved> {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw === null ? {} : (JSON.parse(raw) as Partial<Saved>);
  } catch {
    return {};
  }
}

function save(s: Saved): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(s));
  } catch {
    /* private mode, blocked storage — the settings just do not persist */
  }
}

function mk<K extends keyof HTMLElementTagNameMap>(
  tag: K, cls: string, parent?: HTMLElement,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (parent) parent.appendChild(e);
  return e;
}

export class Menu {
  private readonly root: HTMLDivElement;
  private readonly fpsEl: HTMLSpanElement;
  private readonly whoEl: HTMLDivElement;
  private readonly openBtn: HTMLButtonElement;
  private readonly audioStateEl: HTMLSpanElement;
  private readonly audio: AudioEngine;

  private readonly musicRow: HTMLDivElement;
  private readonly sfxRow: HTMLDivElement;
  private readonly musicSlider: HTMLInputElement;
  private readonly sfxSlider: HTMLInputElement;
  private readonly musicSwitch: HTMLButtonElement;
  private readonly sfxSwitch: HTMLButtonElement;
  private readonly musicPct: HTMLSpanElement;
  private readonly sfxPct: HTMLSpanElement;

  private readonly bayBox: HTMLDivElement;
  private readonly bayCodeEl: HTMLSpanElement;
  private readonly bayUrlEl: HTMLDivElement;
  private readonly bayPeersEl: HTMLSpanElement;
  private readonly copyBtn: HTMLButtonElement;
  private copyTimer: number | null = null;
  private bay = '';

  private readonly nudgeEl: HTMLDivElement;
  private nudgeIn: number | null = null;

  private _open = false;
  private fpsCache = -1;
  private stateCache = '';
  private seenNudge: boolean;
  private touchUi = false;

  constructor(parent: HTMLElement, audio: AudioEngine) {
    this.audio = audio;

    const saved = load();
    audio.setMusicVolume(saved.musicVol ?? 0.7);
    audio.setSfxVolume(saved.sfxVol ?? 0.8);
    audio.setMusicEnabled(saved.musicOn ?? true);
    audio.setSfxEnabled(saved.sfxOn ?? true);
    this.seenNudge = saved.seenNudge ?? false;

    /* Sibling of the panel, not a child: the panel is hidden by `visibility` so its subtree
       leaves the tab order while closed, and the nudge has to be visible precisely then. */
    this.nudgeEl = mk('div', 'nudge', parent);
    mk('b', '', this.nudgeEl).textContent = 'M';
    mk('span', '', this.nudgeEl).textContent = 'MENU & CONTROLS';
    const shut = mk('button', 'nudge-x', this.nudgeEl);
    shut.type = 'button';
    shut.textContent = '×';
    shut.setAttribute('aria-label', 'dismiss');
    shut.addEventListener('click', () => this.dropNudge());

    /* A way in that is not a keyboard. Shown only for a coarse pointer — on a desktop the M
       key and the first-run nudge already cover it, and a permanent button would be exactly the
       always-on corner UI that got removed. */
    this.openBtn = mk('button', 'menu-open', parent);
    this.openBtn.type = 'button';
    this.openBtn.setAttribute('aria-label', 'menu');
    mk('span', '', this.openBtn);
    mk('span', '', this.openBtn);
    mk('span', '', this.openBtn);
    this.openBtn.addEventListener('click', () => {
      /* Another go at the audio, on a real gesture. A phone that refused the first unlock —
         a tab restored in the background, a context suspended while the screen was off — will
         usually take it here, and this is the button somebody presses when they notice. */
      void audio.unlock();
      this.toggle();
    });

    this.root = mk('div', 'menu', parent);

    const head = mk('div', 'menu-head', this.root);
    mk('span', 'menu-blip', head);
    mk('span', 'menu-title', head).textContent = 'SHOREPLAY';
    this.fpsEl = mk('span', 'menu-fps', head);
    this.fpsEl.textContent = '-- FPS';

    // Whose shark this is. The player was asked to name it, so it has to appear somewhere.
    this.whoEl = mk('div', 'menu-who', this.root);

    /* ---- the bay, and the invite ----------------------------------------------------------
       Hidden entirely when solo. An INVITE control on a single-player session is an invitation
       to nothing, and a disabled one is worse: it advertises a feature and then refuses. */
    this.bayBox = mk('div', 'menu-bay', this.root);
    const bayHead = mk('div', 'menu-bay-head', this.bayBox);
    mk('span', 'menu-bay-cap', bayHead).textContent = 'YOUR BAY';
    this.bayPeersEl = mk('span', 'menu-bay-peers', bayHead);
    this.bayCodeEl = mk('span', 'menu-bay-code', this.bayBox);

    /* The link, SHOWN and not merely copyable. A button that says COPY and nothing else asks
       to be trusted about what it put on the clipboard; seeing the URL means it can be checked
       against the code above it, read down a phone, or selected by hand when the clipboard is
       refused. Clicking it selects the whole thing, because a URL is one object and a
       double-click would otherwise take a word of it. */
    this.bayUrlEl = mk('div', 'menu-bay-url', this.bayBox);
    this.bayUrlEl.addEventListener('click', () => {
      const sel = window.getSelection();
      if (!sel) return;
      const r = document.createRange();
      r.selectNodeContents(this.bayUrlEl);
      sel.removeAllRanges();
      sel.addRange(r);
    });

    this.copyBtn = mk('button', 'menu-copy', this.bayBox);
    this.copyBtn.type = 'button';
    this.copyBtn.textContent = 'COPY INVITE LINK';
    this.copyBtn.addEventListener('click', () => void this.copyInvite());

    // ---- audio
    const audioHead = mk('div', 'menu-legend', this.root);
    audioHead.textContent = 'AUDIO';
    /* The context's own state, shown. A browser that refuses to start audio does it silently,
       and without this the only symptom is "no sound" — which is indistinguishable from a bug
       in the synthesiser, a muted bus, or a phone on silent. */
    this.audioStateEl = mk('span', 'menu-audio-state', audioHead);
    const audioBox = mk('div', 'menu-audio', this.root);

    const music = this.buildChannel(audioBox, 'MUSIC');
    this.musicRow = music.row;
    this.musicSlider = music.slider;
    this.musicSwitch = music.sw;
    this.musicPct = music.pct;

    const sfx = this.buildChannel(audioBox, 'AMBIENCE');
    this.sfxRow = sfx.row;
    this.sfxSlider = sfx.slider;
    this.sfxSwitch = sfx.sw;
    this.sfxPct = sfx.pct;

    this.musicSlider.addEventListener('input', () => {
      audio.setMusicVolume(Number(this.musicSlider.value) / 100);
      this.sync();
    });
    this.sfxSlider.addEventListener('input', () => {
      audio.setSfxVolume(Number(this.sfxSlider.value) / 100);
      this.sync();
    });
    this.musicSwitch.addEventListener('click', () => {
      audio.setMusicEnabled(!audio.musicEnabled);
      this.sync();
    });
    this.sfxSwitch.addEventListener('click', () => {
      audio.setSfxEnabled(!audio.sfxEnabled);
      this.sync();
    });

    // ---- controls
    mk('div', 'menu-legend', this.root).textContent = 'CONTROLS';
    const grid = mk('div', 'menu-keys', this.root);
    for (const [key, what] of CONTROLS) {
      mk('span', 'menu-key', grid).textContent = key;
      mk('span', 'menu-act', grid).textContent = what;
    }

    mk('div', 'menu-foot', this.root).textContent = 'M TO CLOSE';

    /* On a phone the panel is a bottom sheet with no key to dismiss it, so it gets its own
       close. On a desktop M does the job and this would be clutter. */
    const closeBtn = mk('button', 'menu-shut', this.root);
    closeBtn.type = 'button';
    closeBtn.textContent = 'CLOSE';
    closeBtn.addEventListener('click', () => this.close());

    this.sync();
  }

  get open(): boolean {
    return this._open;
  }

  /**
   * Show the first-run nudge, once ever. It stays up until the player closes it or opens the
   * menu; there is no timer.
   *
   * `force` is for looking at it during development — otherwise the only way to see it a
   * second time is to clear site data, which is a silly thing to have to do to check a chip.
   */
  showNudge(force = false): void {
    if (this.seenNudge && !force) return;
    this.seenNudge = true;
    this.persist();
    this.nudgeIn = window.setTimeout(() => {
      this.nudgeIn = null;
      // Someone who has already found the menu does not need to be told where it is.
      if (this._open) return;
      this.nudgeEl.classList.add('is-shown');
    }, NUDGE_IN_MS);
  }

  toggle(): void {
    this._open = !this._open;
    this.root.classList.toggle('is-open', this._open);
    // The opener hides while the panel is up: on a phone it would otherwise sit on top of it.
    this.openBtn.classList.toggle('is-shown', this.touchUi && !this._open);
    /* Kill the on-screen stick while the sheet is up. It covers the left half of the screen at
       full height, so without this a thumb reaching for a volume slider steers the shark on the
       way past — and the panel is over the water, so nobody would see why. */
    document.body.classList.toggle('is-menu-open', this._open);
    // Opening the menu answers the nudge's question, so it goes at once rather than sitting
    // underneath the panel telling you about the thing you are already looking at.
    if (this._open) this.dropNudge();
    /* Hand the keyboard back on close. A slider keeps focus after being dragged, and a focused
       range input swallows the arrow keys — which are also the steering keys. */
    if (!this._open && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  close(): void {
    if (this._open) this.toggle();
  }

  /**
   * Which bay this session is in, and how many others are in it. '' hides the whole block.
   * Safe to call every frame — it writes to the DOM only when something actually changed.
   */
  /** the player's own animal, shown once and never updated — it cannot change mid-session */
  setShark(name: string, species: string): void {
    this.whoEl.textContent = name ? `${name} · ${species.toUpperCase()}` : species.toUpperCase();
  }

  setBay(code: string, peers: number, connected: boolean): void {
    if (code !== this.bay) {
      this.bay = code;
      this.bayBox.classList.toggle('is-shown', code !== '');
      this.bayCodeEl.textContent = code === '' ? '' : formatBay(code);
      this.bayUrlEl.textContent = code === '' ? '' : inviteUrl(code);
    }
    if (code === '') return;
    const label = !connected
      ? 'CONNECTING'
      : peers === 0 ? 'JUST YOU' : peers === 1 ? '1 OTHER SHARK' : `${peers} OTHER SHARKS`;
    if (this.bayPeersEl.textContent !== label) this.bayPeersEl.textContent = label;
  }

  /** live while the panel is open; the caller need not check */
  update(fps: number): void {
    if (!this._open) return;

    const st = this.audio.state;
    if (st !== this.stateCache) {
      this.stateCache = st;
      this.audioStateEl.textContent = st === 'running' ? '' : st.toUpperCase();
      this.audioStateEl.classList.toggle('is-bad', st !== 'running');
    }

    const q = Math.round(fps);
    if (q === this.fpsCache) return;
    this.fpsCache = q;
    this.fpsEl.textContent = `${q} FPS`;
  }

  /**
   * Copy the invite link.
   *
   * The clipboard API needs a secure context and a user gesture, and it still refuses in
   * plenty of real situations — an http:// LAN address, a permissions policy, an embedded
   * webview. So there is a fallback that selects the link in a throwaway field, which turns a
   * dead button into a manual copy. Failing silently on a share button is the one outcome that
   * cannot be allowed: the player has no way to tell it did not work.
   */
  private async copyInvite(): Promise<void> {
    if (this.bay === '') return;
    const url = inviteUrl(this.bay);
    let ok = false;
    try {
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch {
      ok = legacyCopy(url);
    }
    this.copyBtn.textContent = ok ? 'COPIED' : 'PRESS CTRL+C';
    this.copyBtn.classList.toggle('is-ok', ok);
    if (!ok) selectText(url);
    if (this.copyTimer !== null) window.clearTimeout(this.copyTimer);
    this.copyTimer = window.setTimeout(() => {
      this.copyBtn.textContent = 'COPY INVITE LINK';
      this.copyBtn.classList.remove('is-ok');
      this.copyTimer = null;
    }, 2200);
  }

  /** show the on-screen way in; the caller decides, so a desktop never sees it */
  useTouchUi(): void {
    this.touchUi = true;
    this.openBtn.classList.toggle('is-shown', !this._open);
  }

  dispose(): void {
    this.openBtn.remove();
    if (this.copyTimer !== null) window.clearTimeout(this.copyTimer);
    this.copyTimer = null;
    this.dropNudge();
    this.nudgeEl.remove();
    this.root.remove();
  }

  private dropNudge(): void {
    if (this.nudgeIn !== null) window.clearTimeout(this.nudgeIn);
    this.nudgeIn = null;
    // Class off, not removed from the tree: the CSS transition is the fade, and an element
    // that is gone cannot fade.
    this.nudgeEl.classList.remove('is-shown');
  }

  private buildChannel(parent: HTMLElement, label: string): {
    row: HTMLDivElement; slider: HTMLInputElement; sw: HTMLButtonElement; pct: HTMLSpanElement;
  } {
    const row = mk('div', 'menu-chan', parent);
    const sw = mk('button', 'menu-sw', row);
    sw.type = 'button';
    mk('span', 'menu-sw-dot', sw);
    mk('span', 'menu-chan-name', row).textContent = label;
    const slider = mk('input', 'menu-slider', row);
    slider.type = 'range';
    slider.min = '0';
    slider.max = '100';
    slider.step = '1';
    const pct = mk('span', 'menu-pct', row);
    return { row, slider, sw, pct };
  }

  /** one place that reads the engine back and makes the panel agree with it */
  private sync(): void {
    const a = this.audio;
    const write = (
      row: HTMLDivElement, slider: HTMLInputElement, sw: HTMLButtonElement,
      pct: HTMLSpanElement, vol: number, on: boolean,
    ): void => {
      const v = String(Math.round(vol * 100));
      if (slider.value !== v) slider.value = v;
      pct.textContent = `${v}%`;
      sw.classList.toggle('is-on', on);
      sw.setAttribute('aria-pressed', on ? 'true' : 'false');
      // The slider stays live while off, so a player can set the level they want it to come
      // back at. Only the row dims, which is what says "this is not being heard right now".
      row.classList.toggle('is-off', !on);
    };
    write(this.musicRow, this.musicSlider, this.musicSwitch, this.musicPct, a.musicVolume, a.musicEnabled);
    write(this.sfxRow, this.sfxSlider, this.sfxSwitch, this.sfxPct, a.sfxVolume, a.sfxEnabled);

    this.persist();
  }

  private persist(): void {
    const a = this.audio;
    save({
      musicVol: a.musicVolume,
      sfxVol: a.sfxVolume,
      musicOn: a.musicEnabled,
      sfxOn: a.sfxEnabled,
      seenNudge: this.seenNudge,
    });
  }
}

/** document.execCommand('copy') on a detached field — deprecated, and still the only thing
    that works when the async clipboard is refused. */
function legacyCopy(text: string): boolean {
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.cssText = 'position:fixed;top:-1000px;opacity:0';
  document.body.appendChild(el);
  el.select();
  let ok = false;
  try { ok = document.execCommand('copy'); } catch { ok = false; }
  el.remove();
  return ok;
}

/** last resort: put the link somewhere the player can copy it by hand */
function selectText(text: string): void {
  const el = document.createElement('input');
  el.className = 'menu-fallback';
  el.value = text;
  el.readOnly = true;
  document.querySelector('.menu')?.appendChild(el);
  el.select();
  el.addEventListener('blur', () => el.remove(), { once: true });
}
